import { formatDate } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { combineLatest, of, Subscription } from 'rxjs';
import { debounceTime, first, map } from 'rxjs/operators';
import { Planning } from 'src/app/classes/planning.class';
import { ProjectComment } from 'src/app/classes/project-comment';
import { Project } from 'src/app/classes/project.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { ProjectService } from 'src/app/services/project.service';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { AsphaltUsedListComponent } from './asphalt-used-list/asphalt-used-list.component';

@Component({
    selector: 'app-project-day-report',
    templateUrl: './project-day-report.component.html',
    styleUrls: ['./project-day-report.component.scss'],
    standalone: false,
})
export class ProjectDayReportComponent extends CodaltComponent implements OnInit {
    projectComments: ProjectComment[];

    form: FormGroup<ControlsOf<{ comment: string }>>;

    saving = false;
    saved = false;

    date = new Date();
    projectId: string;
    project: Project;
    otherProjectsThisDay: Project[];
    projectNotPlannedToday = false;
    loadingAsfalt = false;

    @Input() print = false;

    @ViewChild('asphaltUsedList') asphaltUsedListComponent: AsphaltUsedListComponent;
    @ViewChild('asphaltAdhesiveList') asphaltAdhesiveListComponent: AsphaltUsedListComponent;

    @ViewChild('chooseProjectDialog', { static: true }) chooseProjectDialog;
    projectDialog;
    projectDialogProjects: Project[];

    private currentComment: ProjectComment;

    private navigateProjectSubscription = new Subscription();
    private loadDataSubscription = new Subscription();
    private projectsForDateCache: { date: string; projects: Project[] } | null = null;

    constructor(
        private activatedRoute: ActivatedRoute,
        private planningService: PlanningService,
        private entitiesService: EntitiesService,
        private confirmDialogService: ConfirmDialogService,
        private matDialog: MatDialog,
        private router: Router,
        private cdref: ChangeDetectorRef,
        private projectService: ProjectService
    ) {
        super();
    }

    next() {
        this.date.setDate(this.date.getDate() + 1);
        this.date = new Date(this.date);
        this.changeProjectAndNavigate();
    }

    prev() {
        this.date.setDate(this.date.getDate() - 1);
        this.date = new Date(this.date);
        this.changeProjectAndNavigate();
    }

    otherProjectThisDay() {
        this.projectDialogProjects = this.otherProjectsThisDay;
        this.changeProjectDialog();
    }

    private changeProjectAndNavigate() {
        this.navigateProjectSubscription?.unsubscribe();
        this.navigateProjectSubscription = new Subscription();
        if (!AuthorisationService.hasFunctionality(Functionalities.CommonHourCheck)) {
            this.navigateProjectSubscription.add(
                this.planningService.getProjectsForDate(this.date).subscribe(ds => {
                    this.projectDialogProjects = ds.data;
                    this.projectsForDateCache = {
                        date: Utils.formatDate(this.date),
                        projects: ds.data,
                    };
                    if (
                        !this.projectDialogProjects?.length ||
                        this.projectDialogProjects.map(p => p.afas_project_id).includes(this.projectId)
                    ) {
                        this.router.navigateByUrl(
                            `project-dagrapport/${this.projectId}/${Utils.formatDate(this.date)}`
                        );
                    } else {
                        this.changeProjectDialog();
                    }
                })
            );
        } else {
            this.router.navigateByUrl(`project-dagrapport/${this.projectId}/${Utils.formatDate(this.date)}`);
        }
    }

    private getProjectsForDate() {
        const dateKey = Utils.formatDate(this.date);
        if (this.projectsForDateCache?.date === dateKey) {
            const projects = this.projectsForDateCache.projects;
            this.projectsForDateCache = null;
            return of(projects);
        }
        return this.planningService.getProjectsForDate(this.date).pipe(map(r => r.data));
    }

    private changeProjectDialog() {
        this.projectDialog = this.matDialog.open(this.chooseProjectDialog, {
            disableClose: true,
        });
    }

    chooseProject(project: string) {
        this.projectId = project;
        this.router
            .navigateByUrl(`project-dagrapport/${this.projectId}/${Utils.formatDate(this.date)}`)
            .finally(() => this.projectDialog?.close());
    }

    calculateAdhesives() {
        const list = this.asphaltUsedListComponent.projectUsedAsphaltList;
        const types = [
            { application: 'base', name: 'Modimuls TT' },
            { application: 'bind', name: 'Modimuls TT' },
            { application: 'surf', name: 'Modimuls TT' },
        ];
        types.forEach(type => {
            const typeCount = list
                .filter(l => l.application === type.application)
                .reduce((sum, current) => sum + current.surface, 0);
            if (typeCount > 0) {
                this.asphaltAdhesiveListComponent.projectUsedAsphaltList.push({
                    afas_project_id: this.projectId,
                    date: this.date,
                    surface: typeCount,
                    asphaltname: type.name,
                    liter: Math.round(100 * typeCount * 0.2) / 100,
                    application: 'adhesive_' + type.application,
                });
            }
        });
    }

    copyAsphalt() {
        this.loadingAsfalt = true;
        const nextDay = Utils.newDate(this.date);
        nextDay.setDate(nextDay.getDate() + 1);
        const entities$ = this.entitiesService.getMap();
        const planning$ = this.planningService
            .getFilteredList(this.date, nextDay)
            .pipe(debounceTime(150))
            .pipe(first());
        combineLatest([entities$, planning$]).subscribe(([entitiesMap, plannings]) => {
            const z = plannings
                .filter(planning => planning.asphalt_list?.length > 0)
                .map(planning => {
                    const main = this.planningService.getMainPlanning(planning);
                    main.entity = entitiesMap.get(main.entity_id);
                    let dateString = formatDate(main.begindate, 'EEE dd MMM yyyy HH:mm', 'nl') + ' - ';
                    if (Utils.getDateDate(main.begindate) !== Utils.getDateDate(main.enddate)) {
                        dateString += formatDate(main.enddate, 'EEE dd MMM yyyy', 'nl');
                    }
                    dateString += formatDate(main.enddate, 'HH:mm', 'nl');
                    return {
                        name:
                            planning.afas_project_id +
                            ' - ' +
                            main.entity.name +
                            ' - ' +
                            (planning.asphalt_list?.length ?? 0) +
                            ' asfaltregels',
                        subtext: dateString,
                        planning,
                        main,
                        bgColor: planning.afas_project_id === this.projectId ? '#dedce9' : null,
                    };
                });
            this.loadingAsfalt = false;
            this.confirmDialogService
                .confirm({
                    title: 'Kies een project',
                    text: null,
                    confirm: 'Sluiten',
                    cancel: null,
                    multiSelect: false,
                    options: z,
                })
                .then((result: { planning: Planning }) => {
                    const planning = Array.isArray(result?.planning) ? result.planning[0] : result?.planning;

                    if (planning) {
                        this.asphaltUsedListComponent.projectUsedAsphaltList.push(
                            ...planning.asphalt_list.map(al => {
                                return {
                                    asphaltmill_asphalt_id: al.asphaltmill_asphalt_id,
                                    afas_project_id: this.projectId,
                                    date: this.date,
                                    asphaltmill_id: al.asphaltmill_id,
                                    asphaltname: al.asphaltname,
                                    asphaltcode: al.asphaltcode,
                                    tons: al.tons,
                                    surface: null,
                                    application: null,
                                    thickness: al.thickness,
                                    comment: al.comment,
                                };
                            })
                        );
                    }
                });
        });
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { project; date }) => {
                this.projectId = params.project === 'null' ? null : params.project;
                this.date = params.date ? new Date(params.date) : new Date();
                this.loadData();
            })
        );
    }

    loadData() {
        this.loadDataSubscription?.unsubscribe();
        this.loadDataSubscription = new Subscription();
        this.loadDataSubscription.add(
            this.getProjectsForDate().subscribe(projects => {
                this.otherProjectsThisDay = projects?.filter(p => p.afas_project_id !== this.projectId);
                this.projectNotPlannedToday = this.otherProjectsThisDay?.length === projects?.length;
                if (!this.projectId && this.otherProjectsThisDay?.length > 0) {
                    this.projectId = this.otherProjectsThisDay[0].afas_project_id;
                    this.router.navigateByUrl(`project-dagrapport/${this.projectId}/${Utils.formatDate(this.date)}`);
                }
            })
        );
        if (this.projectId) {
            this.loadDataSubscription.add(
                this.projectService.getProject(this.projectId).subscribe(project => {
                    this.project = project.data;
                })
            );

            this.loadDataSubscription.add(
                this.projectService.getProjectAsphalt(this.projectId, this.date).subscribe(ua => {
                    this.asphaltUsedListComponent.projectUsedAsphaltList = ua.data.filter(
                        pal => pal.application?.substring(0, 9) !== 'adhesive_'
                    );
                    this.asphaltAdhesiveListComponent.projectUsedAsphaltList = ua.data.filter(
                        pal => pal.application?.substring(0, 9) === 'adhesive_'
                    );
                })
            );

            this.loadDataSubscription.add(
                this.projectService.getProjectCommentsWeek(this.projectId, this.date).subscribe(projectComments => {
                    this.currentComment =
                        projectComments.data.find(
                            pc =>
                                Utils.dateString(pc.date) === Utils.dateString(this.date) &&
                                (pc.user_id === LocalStorage.getUser()?.id || !pc.user_id) &&
                                !pc.week
                        ) ?? new ProjectComment();

                    this.form = new FormGroup({
                        comment: new FormControl(this.currentComment?.comment),
                    });
                    this.projectComments = projectComments.data.filter(p => p !== this.currentComment);
                })
            );
        }
    }

    save() {
        Promise.all([
            this.asphaltUsedListComponent.validateForms(),
            this.asphaltAdhesiveListComponent.validateForms(),
        ]).then(
            () => {
                this.saving = true;
                setTimeout(() => {
                    this.currentComment.afas_project_id = this.projectId;
                    this.currentComment.user_id = LocalStorage.getUser()?.id;
                    this.currentComment.date = this.date;
                    this.currentComment.comment = this.form.controls.comment.value;
                    this.subscriptions.add(
                        combineLatest([
                            this.projectService.saveProjectAsphalt(this.projectId, this.date, [
                                ...this.asphaltUsedListComponent.projectUsedAsphaltList,
                                ...this.asphaltAdhesiveListComponent.projectUsedAsphaltList,
                            ]),
                            this.projectService.saveProjectComment(this.currentComment),
                        ]).subscribe(() => {
                            this.saving = false;
                            this.saved = true;
                            setTimeout(() => {
                                this.saved = false;
                            }, 2000);
                            this.loadData();
                        })
                    );
                });
            },
            () => {
                this.confirmDialogService
                    .confirm({
                        title: 'Fout in formulier',
                        text: 'Niet alle velden zijn juist gevuld. Verbeter deze velden en probeer opnieuw op te slaan.',
                        confirm: 'Sluiten',
                        cancel: null,
                    })
                    .then(() => {});
            }
        );
    }
}
